import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { isAdminRoute } from "../utils/authHelpers";
import { BackendUserData, User } from "../types";
import devLogger from "../utils/devLogger";
import { getApiUrl } from "../utils/api";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);

  // Initialize user from localStorage
  useEffect(() => {
    const token = localStorage.getItem("token");
    const details = localStorage.getItem("details");
    if (token && details) {
      setUser({
        ...JSON.parse(details),
        token,
      });
    }
  }, []);

  const login = (userData: BackendUserData) => {
    const user = {
      _id: userData.user._id,
      email: userData.user.email,
      token: userData.token,
      role: userData.user.role,
    };

    setUser(user);
    localStorage.setItem("token", userData.token);
    localStorage.setItem("details", JSON.stringify(userData.user));
  };

  const refreshToken = async () => {
    try {
      const response = await fetch(getApiUrl("/auth/refresh"), {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("token", data.data.accessToken);
      } else {
        logout();
      }
    } catch (error) {
      devLogger.error("Error refreshing token:", error);
      logout();
    }
  };

  const logout = async () => {
    devLogger.log("Logging out");

    await fetch(getApiUrl("/auth/logout"), {
      method: "POST",
      credentials: "include",
    });

    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("details");

    const returnUrl = encodeURIComponent(window.location.pathname);
    window.location.href = isAdminRoute()
      ? `/admin/login?returnUrl=${returnUrl}`
      : `/login?returnUrl=${returnUrl}`;
  };

  const isAuthenticated = !!user;
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isAdmin,
        isSuperAdmin,
        login,
        logout,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

type AuthContextType = {
  user: User | null;
  login: (user: BackendUserData) => void;
  logout: () => void;
  refreshToken: () => Promise<void>;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
};
